"use client";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { IoSearch, IoClose } from "react-icons/io5";
import { CiMoneyBill, CiClock2, CiHome } from "react-icons/ci";
import { RiDeleteBin6Line } from "react-icons/ri";
import InvoicePreviewModal from "./InvoicePreviewModal";
import CalendarDropdown from "./CalendarDropdown";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getUrlWithTimeZone = (baseUrl) => {
  const timeZone = getUserTimeZone();
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}timeZone=${encodeURIComponent(timeZone)}`;
};

const paymentDueOptions = [
  "Within 5 days",
  "Within 10 days",
  "Within 15 days",
  "Within 30 days",
];

const paymentDueMap = {
  "Within 5 days": 5,
  "Within 10 days": 10,
  "Within 15 days": 15,
  "Within 30 days": 30,
};

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getCurrencySymbol = (currencyCode) => {
  return CURRENCY_SYMBOL_MAP[currencyCode] || currencyCode || "$";
};

const formatPrice = (price) => {
  if (!price && price !== 0) return "0";

  const num = parseFloat(price);
  if (isNaN(num)) return "0";

  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    return num.toFixed(0);
  }
};

const getFullPrice = (price) => {
  if (!price && price !== 0) return "0";
  const num = parseFloat(price);
  if (isNaN(num)) return "0";
  return num.toLocaleString("en-US");
};

function OtherServiceModal({ isOpen, onClose, onAdd }) {
  const [serviceName, setServiceName] = useState("");
  const [servicePrice, setServicePrice] = useState("");
  const [serviceCurrency, setServiceCurrency] = useState("USD");
  const modalRef = useRef(null);

  const validateForm = () => {
    const errors = [];

    if (!serviceName.trim()) {
      errors.push("Service name is required");
    }

    if (!servicePrice || isNaN(parseFloat(servicePrice))) {
      errors.push("Please enter valid price");
    }

    return errors;
  };

  const handleSubmit = () => {
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error, { duration: 4000 }));
      return;
    }

    const otherService = {
      id: `other_${Date.now()}`,
      name: serviceName.trim(),
      price: parseFloat(servicePrice),
      currency: serviceCurrency,
      itemType: "other",
      isOther: true,
    };

    onAdd(otherService);
    setServiceName("");
    setServicePrice("");
    setServiceCurrency("USD");
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
        className="w-[400px] max-w-[90%] bg-white rounded-2xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-[#302B2B]">
            Add Custom Service
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-black transition"
          >
            <IoClose size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#302B2B] mb-1">
              Service Name *
            </label>
            <input
              type="text"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full border border-[#9A99AB] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[#5563F6]"
              placeholder="Enter service name"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#302B2B] mb-1">
                Currency
              </label>
              <select
                value={serviceCurrency}
                onChange={(e) => setServiceCurrency(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full border border-[#9A99AB] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[#5563F6]"
              >
                {Object.keys(CURRENCY_SYMBOL_MAP).map((curr) => (
                  <option key={curr} value={curr}>
                    {curr} ({CURRENCY_SYMBOL_MAP[curr]})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#302B2B] mb-1">
                Price *
              </label>
              <input
                type="number"
                value={servicePrice}
                onChange={(e) => setServicePrice(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full border border-[#9A99AB] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[#5563F6]"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 border border-[#EDEEF3] text-[#302B2B] rounded-[14px] py-3 hover:bg-[#F6F6F6] transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 bg-[#5563F6] text-white rounded-[14px] py-3 hover:bg-[#3f4de0] transition"
          >
            Add Service
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function CreateInvoiceModal({ onClose, onCreateSuccess }) {
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [showItemList, setShowItemList] = useState(false);
  const [services, setServices] = useState([]);
  const [listings, setListings] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTab] = useState("services");

  const percentages = [0, 5, 10, 15, 20, 25];
  const [showTax, setShowTax] = useState(false);
  const [taxValue, setTaxValue] = useState(5);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountValue, setDiscountValue] = useState(5);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState("");

  const [showPaymentDue, setShowPaymentDue] = useState(false);
  const [paymentDue, setPaymentDue] = useState("");

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [projectName, setProjectName] = useState("");

  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  const [showOtherModal, setShowOtherModal] = useState(false);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken")
      : null;

  const modalRef = useRef(null);
  const customerListRef = useRef(null);
  const itemListRef = useRef(null);
  const taxRef = useRef(null);
  const discountRef = useRef(null);
  const paymentDueRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isClickInsideModal = modalRef.current?.contains(e.target);
      const isClickInsideCustomerList = customerListRef.current?.contains(
        e.target
      );
      const isClickInsideItemList = itemListRef.current?.contains(e.target);
      const isClickInsideTax = taxRef.current?.contains(e.target);
      const isClickInsideDiscount = discountRef.current?.contains(e.target);
      const isClickInsidePaymentDue = paymentDueRef.current?.contains(e.target);

      if (
        !isClickInsideModal &&
        !isClickInsideCustomerList &&
        !isClickInsideItemList &&
        !isClickInsideTax &&
        !isClickInsideDiscount &&
        !isClickInsidePaymentDue &&
        !showOtherModal
      ) {
        setShowCustomerList(false);
        setShowItemList(false);
        setShowTax(false);
        setShowDiscount(false);
        setShowPaymentDue(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOtherModal]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        if (showOtherModal) {
          setShowOtherModal(false);
        } else if (
          showCustomerList ||
          showItemList ||
          showTax ||
          showDiscount ||
          showPaymentDue
        ) {
          setShowCustomerList(false);
          setShowItemList(false);
          setShowTax(false);
          setShowDiscount(false);
          setShowPaymentDue(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [
    showOtherModal,
    showCustomerList,
    showItemList,
    showTax,
    showDiscount,
    showPaymentDue,
    onClose,
  ]);

  useEffect(() => {
    const today = new Date();
    const formatted = today.toISOString().split("T")[0];
    setInvoiceDate(formatted);
  }, []);

  useEffect(() => {
    if (!showCustomerList) return;

    const fetchClients = async () => {
      try {
        const urlWithTimeZone = getUrlWithTimeZone(`${API_BASE}/client/clients-list/`);
        const res = await axios.get(urlWithTimeZone, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCustomers(res.data);
      } catch (err) {
        console.error("❌ Failed to load clients:", err);
        toast.error("Failed to load clients");
      }
    };

    fetchClients();
  }, [showCustomerList, token]);

  useEffect(() => {
    if (!showItemList) return;

    const fetchData = async () => {
      try {
        const servicesUrlWithTimeZone = getUrlWithTimeZone(`${API_BASE}/service/my-services/`);
        const servicesRes = await axios.get(
          servicesUrlWithTimeZone,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const activeServices = Array.isArray(servicesRes.data)
          ? servicesRes.data.filter((service) => service.on_off === true)
          : [];
        setServices(activeServices);

        const listingsUrlWithTimeZone = getUrlWithTimeZone(`${API_BASE}/service/real-estate-my-listings/`);
        const listingsRes = await axios.get(
          listingsUrlWithTimeZone,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setListings(Array.isArray(listingsRes.data) ? listingsRes.data : []);
      } catch (err) {
        console.error("❌ Failed to load items:", err);
        toast.error("Failed to load items");
      }
    };

    fetchData();
  }, [showItemList, token]);

  const filteredCustomers = customers.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredServices = services.filter((s) =>
    s.name?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const filteredListings = listings.filter((l) =>
    l.property_name?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const handleAddItem = (item, type) => {

    const itemWithType = {
      ...item,
      itemType: type,
      id: item.id || `temp_${Date.now()}`,
    };

    if (
      !selectedItems.some(
        (i) => i.id === itemWithType.id && i.itemType === type
      )
    ) {
      setSelectedItems((prev) => {
        const newItems = [...prev, itemWithType];
        return newItems;
      });
      toast.success(
        `${
          type === "service"
            ? "Service"
            : type === "listing"
            ? "Listing"
            : "Custom service"
        } added`
      );
    } else {
      console.log("Item already exists");
    }

    setShowItemList(false);
    setItemSearch("");
  };

  const handleAddOtherService = (otherService) => {
    if (!selectedItems.some((i) => i.id === otherService.id)) {
      setSelectedItems((prev) => [...prev, otherService]);
      toast.success("Custom service added");
    }
  };

  const handleRemoveItem = (id, type) => {
    setSelectedItems((prev) =>
      prev.filter((item) => !(item.id === id && item.itemType === type))
    );
    toast.success("Item removed");
  };

  const getItemPrice = (item) => {
    if (item.itemType === "service") {
      return item.price || 0;
    } else if (item.itemType === "listing") {
      if (item.listing_type === "Sell") {
        return item.sell_price || 0;
      } else if (item.listing_type === "Rent") {
        return item.monthly_rent_price || 0;
      }
      return item.sell_price || item.monthly_rent_price || 0;
    } else if (item.itemType === "other") {
      return item.price || 0;
    }
    return 0;
  };

  const getFormattedPrice = (item) => {
    const price = getItemPrice(item);
    return formatPrice(price);
  };

  const getFullItemPrice = (item) => {
    const price = getItemPrice(item);
    return getFullPrice(price);
  };

  const getItemCurrencySymbol = (item) => {
    const currencyCode = item.currency || "USD";
    return getCurrencySymbol(currencyCode);
  };

  const getItemCurrencyCode = (item) => {
    return item.currency || "USD";
  };

  const getItemName = (item) => {
    if (item.itemType === "service") {
      return item.name;
    } else if (item.itemType === "listing") {
      return item.property_name;
    } else if (item.itemType === "other") {
      return item.name;
    }
    return "";
  };

  const validateAllFields = () => {
    const errors = [];

    if (!invoiceNumber.trim()) {
      errors.push("Invoice number is required");
    }

    if (!projectName.trim()) {
      errors.push("Project name is required");
    }

    if (!invoiceDate) {
      errors.push("Invoice date is required");
    } else {
      try {
        const d = new Date(invoiceDate);
        if (isNaN(d.getTime())) {
          errors.push("Invalid invoice date");
        }
      } catch (e) {
        errors.push("Invalid invoice date");
      }
    }

    if (!paymentDue) {
      errors.push("Payment due is required");
    } else {
      const paymentDueDays = paymentDueMap[paymentDue];
      if (!paymentDueDays && paymentDue) {
        errors.push("Invalid payment due value");
      }
    }

    if (!selectedCustomer?.id) {
      errors.push("Please select a customer");
    }

    if (selectedItems.length === 0) {
      errors.push("Please add at least one service or listing");
    }

    const otherService = selectedItems.find(
      (item) => item.itemType === "other"
    );
    if (otherService) {
      if (!otherService.price || parseFloat(otherService.price) <= 0) {
        errors.push("Custom service price must be greater than 0");
      }
    }

    return errors;
  };

  const handlePreviewClick = async () => {
    if (!token) {
      toast.error("Authorization token not found");
      return;
    }

    const validationErrors = validateAllFields();
    if (validationErrors.length > 0) {
      validationErrors.forEach((error) =>
        toast.error(error, { duration: 4000 })
      );
      return;
    }

    const paymentDueDays = paymentDueMap[paymentDue];

    let invoiceDateISO = null;
    try {
      const d = new Date(invoiceDate);
      if (!isNaN(d.getTime())) {
        invoiceDateISO = d.toISOString();
      } else {
        toast.error("Invalid invoice date");
        return;
      }
    } catch (e) {
      console.error("❌ Invoice date parse error:", e);
      toast.error("Invalid invoice date");
      return;
    }

    const serviceIds = selectedItems
      .filter((item) => item.itemType === "service")
      .map((item) => item.id);

    const listingIds = selectedItems
      .filter((item) => item.itemType === "listing")
      .map((item) => item.id);

    const otherService = selectedItems.find(
      (item) => item.itemType === "other"
    );

    const payload = {
      invoice_number: invoiceNumber.trim(),
      project_name: projectName.trim(),
      invoice_date: invoiceDateISO,
      payment_due: paymentDueDays,
      customer: selectedCustomer.id,
      listings: listingIds,
      services: serviceIds,
      other_service: !!otherService,
      ...(otherService && {
        other_service_price: parseFloat(otherService.price),
      }),
      tax: String(taxValue),
      discount: String(discountValue),
      paid: false,
    };

    try {
      setPreviewLoading(true);
      const urlWithTimeZone = getUrlWithTimeZone(`${API_BASE}/invoice/invoice-create/`);
      
      const res = await axios.post(
        urlWithTimeZone,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setInvoiceData(res.data);
      setShowPreview(true);
      toast.success("Invoice preview generated successfully");
    } catch (err) {
      console.error("❌ Failed to create invoice:", err);
      console.error("Error response:", err.response?.data);

      const data = err?.response?.data;
      if (data && typeof data === "object") {
        const messages = [];
        Object.values(data).forEach((val) => {
          if (Array.isArray(val)) messages.push(...val);
          else if (typeof val === "string") messages.push(val);
        });
        if (messages.length) {
          messages.forEach((m) => toast.error(m, { duration: 4000 }));
        } else {
          toast.error("Failed to create invoice", { duration: 4000 });
        }
      } else {
        toast.error("Failed to create invoice", { duration: 4000 });
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="w-[700px] max-w-full h-auto bg-white border border-[rgba(154,153,171,0.2)] shadow-md rounded-2xl p-8 flex flex-col gap-6 relative"
        >
          <div className="flex justify-between items-center">
            <h2 className="text-[20px] font-bold text-[#302B2B]">
              Create invoice
            </h2>
            <button
              onClick={onClose}
              className="w-6 h-6 flex items-center justify-center text-gray-700 hover:text-black"
            >
              ✕
            </button>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col flex-1 gap-2">
              <label className="text-sm text-[#302B2B]">Invoice Number *</label>
              <input
                type="text"
                placeholder="Type Invoice number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                className="border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm outline-none focus:border-[#5563F6]"
              />
            </div>
            <div className="flex flex-col flex-1 gap-2">
              <label className="text-sm text-[#302B2B]">Project Name *</label>
              <input
                type="text"
                placeholder="Type project name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm outline-none focus:border-[#5563F6]"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col flex-1 gap-2">
              <label className="text-sm text-[#302B2B]">Invoice Date *</label>
              <CalendarDropdown
                id="invoice-date"
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                value={invoiceDate}
                onChange={setInvoiceDate}
              />
            </div>

            <div
              className="flex flex-col flex-1 gap-2 relative"
              ref={paymentDueRef}
            >
              <label className="text-sm text-[#302B2B]">Payment Due *</label>
              <div
                onClick={() => {
                  setShowPaymentDue((prev) => !prev);
                  setShowTax(false);
                  setShowDiscount(false);
                  setShowCustomerList(false);
                  setShowItemList(false);
                }}
                className="flex items-center justify-between border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm text-[#302B2B] cursor-pointer bg-white hover:border-[#5563F6] transition"
              >
                <span>{paymentDue || "Select payment due"}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    showPaymentDue ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <AnimatePresence>
                {showPaymentDue && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 top-[70px] left-0 w-full bg-white border border-[#EDEEF3] rounded-2xl shadow-lg p-2"
                  >
                    {paymentDueOptions.map((option) => (
                      <div
                        key={option}
                        onClick={() => {
                          setPaymentDue(option);
                          setShowPaymentDue(false);
                        }}
                        className="flex justify-between items-center px-4 py-2 rounded-lg cursor-pointer hover:bg-[#F6F6F6]"
                      >
                        <span className="text-[#302B2B] text-sm">{option}</span>
                        <div
                          className={`w-4 h-4 rounded-full border ${
                            paymentDue === option
                              ? "border-[#5563F6] bg-[#5563F6]"
                              : "border-gray-300"
                          } flex items-center justify-center`}
                        >
                          {paymentDue === option && (
                            <span className="text-white text-[10px]">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex gap-4 relative">
            <div className="flex flex-col flex-1 gap-2">
              <button
                onClick={() => {
                  setShowCustomerList((p) => !p);
                  setShowItemList(false);
                  setShowTax(false);
                  setShowDiscount(false);
                  setShowPaymentDue(false);
                }}
                className="border-2 border-[#5563F6] rounded-[14px] py-3 text-[#5563F6] font-medium flex items-center justify-center gap-2 hover:bg-[#F6F8FF] transition-all"
              >
                {selectedCustomer ? "Change customer" : "Add customer *"}
                <span className="text-lg leading-none">＋</span>
              </button>

              <AnimatePresence>
                {selectedCustomer && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center gap-3 border border-[#EDEEF3] rounded-[14px] px-4 py-3 mt-2"
                  >
                    <img
                      src={
                        selectedCustomer.image_url || "/avatar-placeholder.png"
                      }
                      alt={selectedCustomer.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <span className="flex-1 text-[#302B2B] font-medium">
                      {selectedCustomer.name}
                    </span>
                    <button
                      onClick={() => setSelectedCustomer(null)}
                      className="text-[#9A99AB] hover:text-[#302B2B]"
                    >
                      <IoClose size={18} />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex flex-col flex-1 gap-2">
              <button
                onClick={() => {
                  setShowItemList((p) => !p);
                  setShowCustomerList(false);
                  setShowTax(false);
                  setShowDiscount(false);
                  setShowPaymentDue(false);
                }}
                className="border-2 border-[#5563F6] rounded-[14px] py-3 text-[#5563F6] font-medium flex items-center justify-center gap-2 hover:bg-[#F6F8FF] transition-all"
              >
                Add Service/Listing *
                <span className="text-lg leading-none">＋</span>
              </button>

              <AnimatePresence>
                {selectedItems.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col gap-2 mt-2"
                  >
                    {selectedItems.map((item) => (
                      <div
                        key={`${item.itemType}-${item.id}`}
                        className="flex justify-between items-center border border-[#EDEEF3] rounded-[14px] px-4 py-5"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-[#302B2B] font-medium capitalize text-[12px]">
                            {getItemName(item)}
                            <span className="ml-1 text-xs text-[#9A99AB] capitalize">
                              ({item.itemType})
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-[#302B2B] text-[12px]">
                            {getItemCurrencySymbol(item)}
                            {getFormattedPrice(item)}
                          </span>
                          <button
                            onClick={() =>
                              handleRemoveItem(item.id, item.itemType)
                            }
                            className="text-[#9A99AB] hover:text-[#302B2B]"
                          >
                            <RiDeleteBin6Line size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <AnimatePresence>
              {showCustomerList && (
                <motion.div
                  ref={customerListRef}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 top-[60px] w-[331px] bg-white border border-[rgba(154,153,171,0.2)] rounded-2xl shadow-lg p-4 flex flex-col gap-3 z-50"
                >
                  <div className="bg-[#F6F6F6] rounded-2xl px-4 py-3 flex items-center gap-2">
                    <IoSearch className="text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="bg-transparent outline-none text-sm flex-1 text-[#302B2B]"
                    />
                  </div>
                  <div className="max-h-[250px] overflow-y-auto pr-1 custom-scroll">
                    {filteredCustomers.map((c) => (
                      <div
                        key={c.id}
                        className={`flex items-center gap-3 py-2 px-2 rounded-lg cursor-pointer transition ${
                          selectedCustomer?.id === c.id
                            ? "bg-[#F6F8FF] border border-[#5563F6]"
                            : "hover:bg-[#F6F6F6]"
                        }`}
                        onClick={() => {
                          setSelectedCustomer(c);
                          setShowCustomerList(false);
                        }}
                      >
                        <img
                          src={c.image_url || "/avatar-placeholder.png"}
                          alt={c.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <span className="text-[#302B2B] text-sm">{c.name}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showItemList && (
                <motion.div
                  ref={itemListRef}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 top-[60px] w-[400px] bg-white border border-[rgba(154,153,171,0.2)] rounded-2xl shadow-lg p-4 flex flex-col gap-3 z-50"
                >
                  <div className="flex border-b border-[#EDEEF3]">
                    <button
                      onClick={() => setActiveTab("services")}
                      className={`flex-1 py-2 text-center text-sm font-medium transition ${
                        activeTab === "services"
                          ? "text-[#5563F6] border-b-2 border-[#5563F6]"
                          : "text-[#9A99AB] hover:text-[#302B2B]"
                      }`}
                    >
                      Services
                    </button>
                    <button
                      onClick={() => setActiveTab("listings")}
                      className={`flex-1 py-2 text-center text-sm font-medium transition ${
                        activeTab === "listings"
                          ? "text-[#5563F6] border-b-2 border-[#5563F6]"
                          : "text-[#9A99AB] hover:text-[#302B2B]"
                      }`}
                    >
                      Listings
                    </button>
                  </div>

                  <div className="bg-[#F6F6F6] rounded-2xl px-4 py-3 flex items-center gap-2">
                    <IoSearch className="text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${activeTab}...`}
                      value={itemSearch}
                      onChange={(e) => setItemSearch(e.target.value)}
                      className="bg-transparent outline-none text-sm flex-1 text-[#302B2B]"
                    />
                  </div>

                  <div className="max-h-[300px] overflow-y-auto">
                    {activeTab === "services" ? (
                      <>
                        {filteredServices.length === 0 ? (
                          <div className="text-center py-4 text-[#9A99AB]">
                            No services found
                          </div>
                        ) : (
                          <>
                            {filteredServices.map((s) => (
                              <div
                                key={`service-${s.id}`}
                                onClick={() => handleAddItem(s, "service")}
                                className="border border-[#EDEEF3] rounded-xl p-3 mb-2 hover:bg-[#F6F6F6] cursor-pointer"
                              >
                                <h4 className="font-semibold text-[#302B2B]">
                                  {s.name}
                                </h4>
                                <p className="text-[#9A99AB] text-sm">
                                  {s.description || "No description"}
                                </p>
                                <div className="flex items-center gap-4 text-sm text-[#302B2B] mt-2">
                                  <span className="flex items-center gap-1">
                                    <CiMoneyBill /> Price:{" "}
                                    {getCurrencySymbol(s.currency || "USD")}
                                    {formatPrice(s.price)}
                                    {s.price >= 1000 && (
                                      <span className="ml-1 text-xs text-[#9A99AB]">
                                        ({getFullPrice(s.price)})
                                      </span>
                                    )}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <CiClock2 /> Duration: {s.duration || "—"}{" "}
                                    min
                                  </span>
                                </div>
                              </div>
                            ))}
                            <div
                              onClick={() => {
                                setShowOtherModal(true);
                                setShowItemList(false);
                              }}
                              className="border-2 border-dashed border-[#5563F6] rounded-xl p-4 mb-2 hover:bg-[#F6F8FF] cursor-pointer text-center transition"
                            >
                              <div className="text-[#5563F6] font-medium flex items-center justify-center gap-2">
                                <span className="text-lg">＋</span>
                                <span>Add Other Service</span>
                              </div>
                              <p className="text-[#9A99AB] text-sm mt-1">
                                Create custom service with price
                              </p>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {filteredListings.length === 0 ? (
                          <div className="text-center py-4 text-[#9A99AB]">
                            No listings found
                          </div>
                        ) : (
                          filteredListings.map((l) => (
                            <div
                              key={`listing-${l.id}`}
                              onClick={() => handleAddItem(l, "listing")}
                              className="border border-[#EDEEF3] rounded-xl p-3 mb-2 hover:bg-[#F6F6F6] cursor-pointer"
                            >
                              <h4 className="font-semibold text-[#302B2B]">
                                {l.property_name || "Unnamed Listing"}
                              </h4>
                              <p className="text-[#9A99AB] text-sm">
                                {l.property_description || "No description"}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-[#302B2B] mt-2">
                                <span className="flex items-center gap-1">
                                  <CiMoneyBill />
                                  {l.listing_type === "Rent"
                                    ? `Rent: ${getCurrencySymbol(
                                        l.currency || "USD"
                                      )}${formatPrice(l.monthly_rent_price)}`
                                    : `Sale: ${getCurrencySymbol(
                                        l.currency || "USD"
                                      )}${formatPrice(l.sell_price)}`}
                                  {(l.listing_type === "Rent" &&
                                    l.monthly_rent_price >= 1000) ||
                                  (l.listing_type === "Sell" &&
                                    l.sell_price >= 1000) ? (
                                    <span className="ml-1 text-xs text-[#9A99AB]">
                                      (
                                      {getFullPrice(
                                        l.listing_type === "Rent"
                                          ? l.monthly_rent_price
                                          : l.sell_price
                                      )}
                                      )
                                    </span>
                                  ) : null}
                                </span>
                                <span className="flex items-center gap-1">
                                  <CiHome /> Type: {l.listing_type || "—"}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col flex-1 gap-2 relative" ref={taxRef}>
              <label className="text-sm text-[#302B2B]">Tax</label>
              <div
                onClick={() => {
                  setShowTax((prev) => !prev);
                  setShowDiscount(false);
                  setShowCustomerList(false);
                  setShowItemList(false);
                  setShowPaymentDue(false);
                }}
                className="flex items-center justify-between border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm text-[#302B2B] cursor-pointer bg-white hover:border-[#5563F6] transition"
              >
                <span>{`TAX ${taxValue}%`}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    showTax ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <AnimatePresence>
                {showTax && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 top-[70px] left-0 w-full bg-white border border-[#EDEEF3] rounded-2xl shadow-lg p-2"
                  >
                    {percentages.map((p) => (
                      <div
                        key={p}
                        onClick={() => {
                          setTaxValue(p);
                          setShowTax(false);
                        }}
                        className="flex justify-between items-center px-4 py-2 rounded-lg cursor-pointer hover:bg-[#F6F6F6]"
                      >
                        <span className="text-[#302B2B] text-sm">{p}%</span>
                        <div
                          className={`w-4 h-4 rounded-full border ${
                            taxValue === p
                              ? "border-[#5563F6] bg-[#5563F6]"
                              : "border-gray-300"
                          } flex items-center justify-center`}
                        >
                          {taxValue === p && (
                            <span className="text-white text-[10px]">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div
              className="flex flex-col flex-1 gap-2 relative"
              ref={discountRef}
            >
              <label className="text-sm text-[#302B2B]">Discount</label>
              <div
                onClick={() => {
                  setShowDiscount((prev) => !prev);
                  setShowTax(false);
                  setShowCustomerList(false);
                  setShowItemList(false);
                  setShowPaymentDue(false);
                }}
                className="flex items-center justify-between border border-[#9A99AB] rounded-[14px] py-3 px-3 text-sm text-[#302B2B] cursor-pointer bg-white hover:border-[#5563F6] transition"
              >
                <span>{`Discount ${discountValue}%`}</span>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    showDiscount ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <AnimatePresence>
                {showDiscount && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 top-[70px] left-0 w-full bg-white border border-[#EDEEF3] rounded-2xl shadow-lg p-2"
                  >
                    {percentages.map((p) => (
                      <div
                        key={p}
                        onClick={() => {
                          setDiscountValue(p);
                          setShowDiscount(false);
                        }}
                        className="flex justify-between items-center px-4 py-2 rounded-lg cursor-pointer hover:bg-[#F6F6F6]"
                      >
                        <span className="text-[#302B2B] text-sm">{p}%</span>
                        <div
                          className={`w-4 h-4 rounded-full border ${
                            discountValue === p
                              ? "border-[#5563F6] bg-[#5563F6]"
                              : "border-gray-300"
                          } flex items-center justify-center`}
                        >
                          {discountValue === p && (
                            <span className="text-white text-[10px]">✓</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-auto">
            <button
              onClick={onClose}
              className="text-[#9A99AB] text-[16px] hover:text-[#302B2B]"
            >
              Cancel
            </button>
            <button
              onClick={handlePreviewClick}
              disabled={previewLoading}
              className="bg-[#5563F6] text-white rounded-[14px] px-6 py-3 hover:bg-[#3f4de0] disabled:opacity-50"
            >
              {previewLoading ? "Generating..." : "Preview"}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Other Service Modal */}
      <OtherServiceModal
        isOpen={showOtherModal}
        onClose={() => setShowOtherModal(false)}
        onAdd={handleAddOtherService}
      />

      <AnimatePresence>
        {showPreview && invoiceData && (
          <InvoicePreviewModal
            invoice={invoiceData}
            onBack={() => setShowPreview(false)}
            onClose={() => {
              setShowPreview(false);
              onClose();
              onCreateSuccess?.();
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
